import { useState } from 'react'
import {
  Button,
  Typography,
  Dialog,
  Input,
  Spinner,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from '@material-tailwind/react'
import { PiShareNetworkLight } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { logger } from '../utils/helpers/logger'
import { shareExperience } from '../utils/helpers/supabase.helpers'
import { useGeneralToast } from '../hooks/toast'
import { useSessionContext } from '../contexts/SessionContext'
import { useDevice } from '../hooks/device'
import { useExperienceSharingInfo } from '../hooks/useExperienceSharingInfo'
import { useDarkMode } from '../contexts/DarkModeContext'
import SharingRoleSelector from './SharingRoleSelector'
import SharingList, { type Sharing } from './SharingList'
import ExperienceNotificationsBlock from './ExperienceNotificationsBlock'
import { AgamiExperience } from '../types/supabase'

interface ShareExperiencePanelProps {
  // The whole experience, not just id and name: the notifications block reads
  // and saves notify_enabled / notify_emails. The sidebar already passed it all.
  experience: AgamiExperience
  onSuccess: () => void
}

const ShareExperiencePanel: React.FC<ShareExperiencePanelProps> = ({ experience, onSuccess }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { platformType } = useDevice() || {}

  const { success: toastSuccess, error: toastError } = useGeneralToast()

  const { user } = useSessionContext()

  const [loading, setLoading] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [emailError, setEmailError] = useState(false)
  const [targetRole, setTargetRole] = useState<string | undefined>()
  const [isEditing, setIsEditing] = useState(false)
  // Role the share already has, kept to preselect it and to detect a no-op update.
  const [editingRole, setEditingRole] = useState<number | undefined>()

  const [sharingUpsertData, setSharingUpsertData] = useState<{
    experienceId: string
    email: string
    sharingId: string | null
  }>({
    experienceId: experience.id,
    email: '',
    sharingId: null,
  })

  const { experienceSharingInfo, isLoading, refetch } = useExperienceSharingInfo({
    experienceId: experience?.id,
  })

  const performShare = async (revoke: boolean = false) => {
    if (sharingUpsertData.email === user?.email) {
      toastError(t('sharing.errors.cantShareWithSelf'))
      return
    }

    setLoading(true)
    setEmailError(false)

    const { error } = await shareExperience({
      ...sharingUpsertData,
      role: Number(targetRole),
      revoke,
    })

    if (error) {
      logger.error(error, 'ShareExperience')
      if (error === 'User not found') {
        setEmailError(true)
      }
      toastError(typeof error === 'string' ? error : t('sharing.errors.generalError'))
      setLoading(false)
      return
    }

    toastSuccess(revoke ? t('sharing.errors.revokedSuccess') : t('sharing.errors.sharedSuccess'))
    setShowDialog(false)
    setLoading(false)
    refetch()
    if (onSuccess) onSuccess()
  }

  const startNewSharing = () => {
    setIsEditing(false)
    setEditingRole(undefined)
    setTargetRole(undefined)
    setSharingUpsertData((prev) => ({
      ...prev,
      sharingId: null,
      email: '',
    }))
    setShowDialog(true)
  }

  const performNewSharing = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    performShare(false)
  }

  const startEditSharing = ({ userEmail, sharingId, role }: Sharing) => {
    setIsEditing(true)
    setEditingRole(role)
    setTargetRole(role != null ? String(role) : undefined)
    setSharingUpsertData((prev) => ({
      ...prev,
      sharingId: sharingId || null,
      email: userEmail,
    }))
    setShowDialog(true)
  }

  const performEditSharing = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const revoke = (e.nativeEvent as SubmitEvent)?.submitter?.dataset?.revoke === 'true'

    // Nothing changed: don't call. The role whitelist rejects roles that are no
    // longer delegable, so resubmitting an untouched `editor` share would answer
    // 400 for an operation the owner never asked for. The fix belongs here, not in
    // the backend: a whitelist with exceptions is not a whitelist.
    if (!revoke && Number(targetRole) === editingRole) {
      setShowDialog(false)
      return
    }

    performShare(revoke)
  }

  return (
    <>
      <Dialog
        size={platformType === 'mobile' ? 'xl' : 'xs'}
        open={showDialog}
        handler={() => setShowDialog((cur) => !cur)}
        className="shadow-none"
      >
        <DialogHeader data-testid="sharing-dialog">
          <Typography variant="h4">{t('sharing.dialogTitle')}</Typography>
        </DialogHeader>

        <form className="w-full" onSubmit={isEditing ? performEditSharing : performNewSharing}>
          <DialogBody className="flex flex-col gap-4">
            {isEditing && (
              <Typography className="mb-3 font-normal" variant="paragraph">
                {t('sharing.editDescription')}
              </Typography>
            )}

            {!isEditing && (
              <Typography className="mb-3 font-normal" variant="paragraph">
                {t('sharing.newDescription')}
              </Typography>
            )}
            <Input
              data-testid="sharing-email-input"
              value={sharingUpsertData.email}
              label={t('sharing.email')}
              size="lg"
              onChange={(e) => {
                setEmailError(false)
                setSharingUpsertData((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }}
              disabled={loading}
              readOnly={isEditing}
              error={emailError}
              required
              {...(darkMode && { color: 'white' })}
            />
            <SharingRoleSelector
              value={targetRole}
              currentRole={editingRole}
              disabled={loading}
              darkMode={darkMode}
              onChange={setTargetRole}
            />
          </DialogBody>
          <DialogFooter className="pt-0">
            {isEditing && (
              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  data-testid="sharing-update-btn"
                  size="sm"
                  fullWidth
                  disabled={loading}
                  className="flex justify-center whitespace-nowrap"
                >
                  {loading ? <Spinner className="h-4 w-4" /> : t('sharing.updateRole')}
                </Button>
                <Button
                  type="submit"
                  data-testid="sharing-revoke-btn"
                  data-revoke
                  color="red"
                  size="sm"
                  fullWidth
                  disabled={loading}
                  className="flex justify-center"
                >
                  {loading ? <Spinner className="h-4 w-4" /> : t('sharing.revoke')}
                </Button>
              </div>
            )}

            {!isEditing && (
              <Button
                type="submit"
                data-testid="sharing-submit-btn"
                fullWidth
                disabled={loading}
                className="flex justify-center"
              >
                {loading ? <Spinner className="h-4 w-4" /> : t('sharing.share')}
              </Button>
            )}
          </DialogFooter>
        </form>

        {/* </Card> */}
      </Dialog>

      <div className="flex items-center justify-start gap-4">
        <Typography variant="h5">{t('sharing.title')}</Typography>
        <Button size="sm" data-testid="share-experience-btn" disabled={loading} onClick={startNewSharing}>
          <PiShareNetworkLight />
        </Button>
      </div>

      <div className="my-4">
        <SharingList
          sharingInfo={experienceSharingInfo}
          isLoading={isLoading}
          darkMode={darkMode}
          onEdit={startEditSharing}
        />
      </div>

      <ExperienceNotificationsBlock experience={experience} darkMode={darkMode} onSaved={onSuccess} />
    </>
  )
}
export default ShareExperiencePanel
